'use client';
import { jsxs, jsx, Fragment } from '@emotion/react/jsx-runtime';
import { useState, useRef, useMemo } from 'react';
import { Transition } from '@s7/base';
import { useUncontrolled } from '@s7/hooks';
import { secretlyPrevent, composeHandlers } from '@s7/react-utils';
import { getDefaultZIndex } from '@s7/styles';
import { useInputProps, InputPopover, Input, Loader } from '@s7/ui';
import { COMPONENT_ID, defaultProps } from './FooterSearch.contract.js';
import { filterNavigationItems } from './utils/filterNavigationItems.js';
import { sliceItems } from './utils/sliceItems.js';
import { SearchLayout } from './layouts/SearchLayout/SearchLayout.js';
import { openResource } from '../../../common/ResourceLink/ResourceLink.js';
import { InputResetItem } from './slots/InputResetItem/InputResetItem.js';
import { countItems } from './utils/countItems.js';

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
var FooterSearch = function(props) {
    var _useInputProps = useInputProps(COMPONENT_ID, defaultProps, props), _useInputProps_defaultOpened = _useInputProps.defaultOpened, defaultOpened = _useInputProps_defaultOpened === void 0 ? false : _useInputProps_defaultOpened, navigation = _useInputProps.navigation, helpCenter = _useInputProps.helpCenter, loading = _useInputProps.loading, showMore = _useInputProps.showMore, renderSearchFallback = _useInputProps.renderSearchFallback, onShowMoreClick = _useInputProps.onShowMoreClick, onNavigationItemClick = _useInputProps.onNavigationItemClick, onItemClick = _useInputProps.onItemClick, onShowMoreSelect = _useInputProps.onShowMoreSelect, onNavigationItemSelect = _useInputProps.onNavigationItemSelect, onItemSelect = _useInputProps.onItemSelect, value = _useInputProps.value, defaultValue = _useInputProps.defaultValue, onChange = _useInputProps.onChange, onInputReset = _useInputProps.onInputReset, inputProps = _useInputProps.inputProps, styles = _useInputProps.styles, classNames = _useInputProps.classNames, others = _object_without_properties_loose(_useInputProps, [
        "defaultOpened",
        "navigation",
        "helpCenter",
        "loading",
        "showMore",
        "renderSearchFallback",
        "onShowMoreClick",
        "onNavigationItemClick",
        "onItemClick",
        "onShowMoreSelect",
        "onNavigationItemSelect",
        "onItemSelect",
        "value",
        "defaultValue",
        "onChange",
        "onInputReset",
        "inputProps",
        "styles",
        "classNames"
    ]);
    var _useState = useState(-1), hovered = _useState[0], setHovered = _useState[1];
    var _useState1 = useState(function() {
        return defaultOpened;
    }), dropdownOpened = _useState1[0], setDropdownOpened = _useState1[1];
    var _useState2 = useState("bottom"), position = _useState2[0], setPosition = _useState2[1];
    var _useState3 = useState(false), IMESession = _useState3[0], setIMESession = _useState3[1];
    var inputRef = useRef(null);
    var _useUncontrolled = useUncontrolled({
        value: value,
        defaultValue: defaultValue,
        finalValue: "",
        onChange: onChange
    }), inputValue = _useUncontrolled[0], handleInputValueChange = _useUncontrolled[1];
    var visibleNavigationItems = useMemo(function() {
        return filterNavigationItems(navigation, inputValue);
    }, [
        navigation
    ]);
    var visibleHelpCenterItems = useMemo(function() {
        return sliceItems(helpCenter == null ? void 0 : helpCenter.items, helpCenter == null ? void 0 : helpCenter.maxItems);
    }, [
        helpCenter
    ]);
    var totalItems = countItems(visibleNavigationItems, visibleHelpCenterItems, showMore);
    var isInputDirty = inputValue.length > 0;
    var startIMESession = function() {
        return setIMESession(true);
    };
    var endIMESession = function() {
        return setIMESession(false);
    };
    var handleInputClick = function() {
        return setDropdownOpened(true);
    };
    var handleInputFocus = function() {
        return setDropdownOpened(true);
    };
    var handleInputBlur = function() {
        return setDropdownOpened(false);
    };
    var handleInputKeydown = function(event) {
        if (IMESession) {
            return;
        }
        switch(event.key){
            case "ArrowUp":
                {
                    event.preventDefault();
                    if (totalItems) {
                        setHovered(function(current) {
                            return current > 0 ? current - 1 : totalItems - 1;
                        });
                    }
                    break;
                }
            case "ArrowDown":
                {
                    event.preventDefault();
                    if (totalItems) {
                        setHovered(function(current) {
                            return current < totalItems - 1 ? current + 1 : 0;
                        });
                    }
                    break;
                }
            case "Enter":
                {
                    secretlyPrevent(event);
                    setDropdownOpened(true);
                    var isShowMoreSelected = hovered + 1 === totalItems;
                    if (isShowMoreSelected) {
                        composeHandlers(onShowMoreSelect, function() {
                            return (showMore == null ? void 0 : showMore.link) && openResource(showMore.link);
                        })(event);
                        break;
                    }
                    if (visibleNavigationItems) {
                        var item = visibleNavigationItems[hovered];
                        if (item) {
                            composeHandlers(onNavigationItemSelect, function() {
                                return (item == null ? void 0 : item.link) && openResource(item.link);
                            }, {
                                pickEventFromArgs: function() {
                                    for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
                                        args[_key] = arguments[_key];
                                    }
                                    return args[1];
                                }
                            })(item, event, hovered);
                            break;
                        }
                    }
                    if (visibleHelpCenterItems) {
                        var itemIndex = (visibleNavigationItems == null ? void 0 : visibleNavigationItems.length) ? hovered - visibleNavigationItems.length : hovered;
                        var item1 = visibleHelpCenterItems[itemIndex];
                        if (item1) {
                            composeHandlers(onItemSelect, function() {
                                return (item1 == null ? void 0 : item1.link) && openResource(item1.link);
                            }, {
                                pickEventFromArgs: function() {
                                    for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
                                        args[_key] = arguments[_key];
                                    }
                                    return args[1];
                                }
                            })(item1, event, hovered);
                            break;
                        }
                    }
                    break;
                }
            case "Escape":
                {
                    if (dropdownOpened) {
                        event.preventDefault();
                        setDropdownOpened(false);
                    }
                }
        }
    };
    var handleInputReset = function(event) {
        var _inputRef_current_focus, _inputRef_current;
        event.preventDefault();
        handleInputValueChange("");
        onInputReset == null ? void 0 : onInputReset();
        inputRef == null ? void 0 : (_inputRef_current = inputRef.current) == null ? void 0 : (_inputRef_current_focus = _inputRef_current.focus) == null ? void 0 : _inputRef_current_focus.call(_inputRef_current);
    };
    var handleInputResetKeydown = function(event) {
        if (event.key === "Escape") {
            var _inputRef_current_focus, _inputRef_current;
            event.preventDefault();
            inputRef == null ? void 0 : (_inputRef_current = inputRef.current) == null ? void 0 : (_inputRef_current_focus = _inputRef_current.focus) == null ? void 0 : _inputRef_current_focus.call(_inputRef_current);
            onInputReset == null ? void 0 : onInputReset();
        }
    };
    var InputRightSlot = isInputDirty ? /*#__PURE__*/ jsxs(Fragment, {
        children: [
            /*#__PURE__*/ jsx(Transition, {
                mounted: loading || false,
                transition: "fade",
                children: function(style) {
                    return /*#__PURE__*/ jsx(Loader, {
                        color: "primary.mid",
                        size: "sm1",
                        style: style
                    });
                }
            }),
            /*#__PURE__*/ jsx(InputResetItem, {
                onClick: handleInputReset,
                onKeyDown: handleInputResetKeydown
            })
        ]
    }) : undefined;
    var shouldRenderDropdown = dropdownOpened;
    var activeDescendant = hovered >= 0 ? inputProps.id + "-" + hovered : undefined;
    var controls = shouldRenderDropdown ? "" + inputProps.id + "-items" : undefined;
    var owns = shouldRenderDropdown ? "" + inputProps.id + "-items" : undefined;
    return /*#__PURE__*/ jsxs(InputPopover, {
        __staticSelector: COMPONENT_ID,
        classNames: classNames,
        dropdownPosition: "flip",
        offset: -68,
        opened: shouldRenderDropdown,
        styles: styles,
        zIndex: getDefaultZIndex("modal") - 11,
        onPositionChange: setPosition,
        children: [
            /*#__PURE__*/ jsx(InputPopover.Target, {
                children: /*#__PURE__*/ jsx(Input.Root, {
                    __staticSelector: COMPONENT_ID,
                    classNames: classNames,
                    styles: styles,
                    children: /*#__PURE__*/ jsx(Input, _extends({
                        ref: inputRef,
                        autoComplete: "off",
                        type: "search"
                    }, others, dropdownOpened && {
                        sx: {
                            zIndex: getDefaultZIndex("modal") - 10
                        }
                    }, {
                        __staticSelector: COMPONENT_ID,
                        "aria-activedescendant": activeDescendant,
                        "aria-autocomplete": "list",
                        "aria-controls": controls,
                        "aria-expanded": shouldRenderDropdown,
                        "aria-haspopup": "listbox",
                        "aria-owns": owns,
                        classNames: classNames,
                        rightSide: InputRightSlot,
                        role: "combobox",
                        styles: styles,
                        value: inputValue,
                        onChange: function(event) {
                            handleInputValueChange(event.currentTarget.value);
                            setDropdownOpened(true);
                        },
                        onBlur: handleInputBlur,
                        onClick: handleInputClick,
                        onCompositionEnd: endIMESession,
                        onCompositionStart: startIMESession,
                        onFocus: handleInputFocus,
                        onKeyDown: handleInputKeydown
                    }))
                })
            }),
            /*#__PURE__*/ jsx(InputPopover.Dropdown, {
                classNames: classNames,
                id: inputProps.id,
                maxHeight: "min-content",
                children: /*#__PURE__*/ jsx(SearchLayout, {
                    helpCenter: {
                        title: helpCenter == null ? void 0 : helpCenter.title,
                        items: visibleHelpCenterItems
                    },
                    navigation: {
                        title: navigation == null ? void 0 : navigation.title,
                        items: visibleNavigationItems
                    },
                    "data-position-bottom": position === "bottom",
                    highlight: inputValue,
                    hovered: hovered,
                    loading: loading,
                    renderSearchFallback: renderSearchFallback,
                    showMore: showMore,
                    totalItems: totalItems,
                    onItemClick: onItemClick,
                    onItemHover: setHovered,
                    onNavigationItemClick: onNavigationItemClick,
                    onShowMoreClick: onShowMoreClick
                })
            })
        ]
    });
};

export { FooterSearch };
